import get from 'lodash/get';

/**
 * Relevance tier for sorting: lower = better (exact title match before substring noise).
 * @param {string|null|undefined} name
 * @param {string} query
 * @returns {number} 0 exact, 1 startsWith, 2 contains, 99 no match
 */
export function productNameRankValue(name, query) {
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return 0;
  const n = String(name ?? '').trim().toLowerCase();
  if (!n.includes(q)) return 99;
  if (n === q) return 0;
  if (n.startsWith(q)) return 1;
  return 2;
}

/**
 * Sort products so exact name matches (e.g. "BEER") appear before substring-only matches.
 * @param {Array<object>} products
 * @param {string} query
 * @param {(p: object) => string} [getName]
 * @returns {Array<object>}
 */
export function sortByProductNameRelevance(products, query, getName = (p) => p.name) {
  const q = (query ?? '').trim();
  if (!q) return [...products];
  return [...products].sort((a, b) => {
    const ra = productNameRankValue(getName(a), q);
    const rb = productNameRankValue(getName(b), q);
    if (ra !== rb) return ra - rb;
    return String(getName(a) ?? '').localeCompare(String(getName(b) ?? ''));
  });
}

/**
 * After Fuse search, move rows whose primary field exactly matches the query first.
 * Preserves relative Fuse order within each group.
 * @param {Array<{ item: object, score?: number }>} fuseResults
 * @param {string} query
 * @param {string|string[]} primaryKey lodash path, e.g. 'name' or ['user','name']
 * @returns {Array<object>} items in display order
 */
export function partitionFuseResultsByPrimaryExactMatch(fuseResults, query, primaryKey) {
  if (!primaryKey || !(query ?? '').trim()) {
    return fuseResults.map(({ item }) => item);
  }
  const q = query.trim().toLowerCase();
  const exact = [];
  const rest = [];
  fuseResults.forEach((r) => {
    const val = get(r.item, primaryKey);
    const str = val != null ? String(val).trim().toLowerCase() : '';
    if (str === q) exact.push(r);
    else rest.push(r);
  });
  return [...exact, ...rest].map(({ item }) => item);
}
